import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings,
  Save,
  RotateCcw,
  ChevronLeft,
  FileText,
  Table,
  Search,
  MessageSquare,
  Check,
  AlertCircle,
  Plus,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  Activity,
} from 'lucide-react';
import toast from 'react-hot-toast';
import promptService, { DEFAULT_PROMPTS } from '../../../services/promptService';

// Tool icon mapping
const TOOL_ICONS = {
  table: Table,
  document: FileText,
  search: Search,
  default: MessageSquare,
};

// Tool labels
const TOOL_LABELS = {
  table: 'Table Tool',
  document: 'Document Tool',
  search: 'Search Tool',
  default: 'Default (General)',
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState({});
  const [activeTab, setActiveTab] = useState('default');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check admin permission
  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    if (userRole !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    setIsAdmin(true);
    loadPrompts();
  }, [navigate]);

  const loadPrompts = async () => {
    // Initialize from backend first
    await promptService.initialize();
    const loadedPrompts = promptService.getAllPrompts();
    setPrompts(loadedPrompts);
    setHasChanges(false);
  };

  const handlePromptChange = (tool, value) => {
    setPrompts((prev) => ({
      ...prev,
      [tool]: value,
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const success = await promptService.updateAllPrompts(prompts);
      if (success) {
        toast.success('Prompts saved to database');
        setHasChanges(false);
      } else {
        toast.success('Prompts saved locally (backend unavailable)');
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('An error occurred while saving');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = (tool = null) => {
    if (tool) {
      const defaultPrompt = promptService.getDefaultPrompt(tool);
      setPrompts((prev) => ({
        ...prev,
        [tool]: defaultPrompt,
      }));
      toast.success(`${TOOL_LABELS[tool] || tool} reset to default`);
    } else {
      const defaultPrompts = promptService.getAllDefaultPrompts();
      setPrompts(defaultPrompts);
      toast.success('All prompts reset to default');
    }
    setHasChanges(true);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toolKeys = Object.keys(prompts);

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-bg-100">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-bg-100/80 backdrop-blur-sm border-b border-bg-300/30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="p-2 rounded-lg hover:bg-bg-300/50 text-text-300 hover:text-text-100 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-sedaily-500" />
              <h1 className="text-lg font-semibold text-text-100">Admin - Prompt Management</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/admin/usage')}
              className="px-3 py-1.5 text-sm rounded-lg text-text-300 hover:text-text-100 hover:bg-bg-300/50 transition-colors flex items-center gap-1.5"
            >
              <Activity className="w-4 h-4" />
              Usage
            </button>
            {hasChanges && (
              <span className="text-xs text-amber-500 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                Unsaved changes
              </span>
            )}
            <button
              onClick={() => handleReset()}
              className="px-3 py-1.5 text-sm rounded-lg text-text-300 hover:text-text-100 hover:bg-bg-300/50 transition-colors flex items-center gap-1.5"
            >
              <RotateCcw className="w-4 h-4" />
              Reset All
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className={`px-4 py-1.5 text-sm rounded-lg flex items-center gap-1.5 transition-colors ${
                hasChanges
                  ? 'bg-sedaily-500 text-white hover:bg-sedaily-600'
                  : 'bg-bg-300 text-text-500 cursor-not-allowed'
              }`}
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Tool Tabs (Left) */}
          <div className="w-56 flex-shrink-0">
            <div className="sticky top-20 bg-bg-200/50 rounded-xl p-2 space-y-1">
              <div className="px-3 py-2 text-xs font-medium text-text-500 uppercase tracking-wider">
                Prompt Tools
              </div>
              {toolKeys.map((tool) => {
                const Icon = TOOL_ICONS[tool] || MessageSquare;
                const label = TOOL_LABELS[tool] || tool;
                const isActive = activeTab === tool;
                const isModified = prompts[tool] !== DEFAULT_PROMPTS[tool];

                return (
                  <button
                    key={tool}
                    onClick={() => setActiveTab(tool)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'bg-sedaily-500/10 text-sedaily-500'
                        : 'text-text-300 hover:text-text-100 hover:bg-bg-300/50'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 text-left truncate">{label}</span>
                    {isModified && (
                      <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Editor (Right) */}
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-bg-200/50 rounded-xl p-6"
              >
                {/* Tool Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    {(() => {
                      const Icon = TOOL_ICONS[activeTab] || MessageSquare;
                      return <Icon className="w-5 h-5 text-sedaily-500" />;
                    })()}
                    <h2 className="text-lg font-medium text-text-100">
                      {TOOL_LABELS[activeTab] || activeTab}
                    </h2>
                    {prompts[activeTab] !== DEFAULT_PROMPTS[activeTab] && (
                      <span className="px-2 py-0.5 text-xs bg-amber-500/10 text-amber-500 rounded-full">
                        Modified
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="p-2 rounded-lg text-text-400 hover:text-text-200 hover:bg-bg-300/50 transition-colors"
                      title={showPreview ? 'Hide Preview' : 'Show Preview'}
                    >
                      {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleCopy(prompts[activeTab])}
                      className="p-2 rounded-lg text-text-400 hover:text-text-200 hover:bg-bg-300/50 transition-colors"
                      title="Copy"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleReset(activeTab)}
                      className="p-2 rounded-lg text-text-400 hover:text-text-200 hover:bg-bg-300/50 transition-colors"
                      title="Reset to Default"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Prompt Editor */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-300 mb-2">
                      System Prompt
                    </label>
                    <textarea
                      value={prompts[activeTab] || ''}
                      onChange={(e) => handlePromptChange(activeTab, e.target.value)}
                      className="w-full h-64 bg-bg-100 text-text-200 text-sm px-4 py-3 rounded-lg border border-bg-300/30 outline-none focus:ring-2 focus:ring-sedaily-500/30 resize-none font-mono leading-relaxed"
                      placeholder="Enter system prompt..."
                    />
                    <div className="mt-2 flex items-center justify-between text-xs text-text-500">
                      <span>{prompts[activeTab]?.length || 0} characters</span>
                      <span>
                        {prompts[activeTab]?.split(/\s+/).filter(Boolean).length || 0} words
                      </span>
                    </div>
                  </div>

                  {/* Preview */}
                  <AnimatePresence>
                    {showPreview && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="bg-bg-100 rounded-lg p-4 border border-bg-300/30">
                          <div className="text-xs font-medium text-text-500 uppercase tracking-wider mb-2">
                            Preview
                          </div>
                          <div className="text-sm text-text-300 whitespace-pre-wrap font-mono">
                            {prompts[activeTab] || 'No prompt content'}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Default Prompt Reference */}
                  <div className="bg-bg-100/50 rounded-lg p-4 border border-bg-300/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-text-500 uppercase tracking-wider">
                        Default Prompt (Reference)
                      </span>
                      <button
                        onClick={() => handleCopy(DEFAULT_PROMPTS[activeTab] || '')}
                        className="text-xs text-text-400 hover:text-text-200 flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" />
                        Copy
                      </button>
                    </div>
                    <div className="text-xs text-text-500 whitespace-pre-wrap font-mono max-h-32 overflow-y-auto">
                      {DEFAULT_PROMPTS[activeTab] || 'No default prompt'}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;